import type { KlGridFormColumn } from './kl-grid-form';
import type {
  KlGridFormPastedCodeTarget,
  KlGridFormPastedRow,
  KlGridFormPastedValue,
  KlGridFormPasteEvent,
} from './kl-grid-form.models';

/** Reads `raw` with the locale's own separators (tr: 1.234,56 · en: 1,234.56); `null` for empty or non-numeric text. */
export function parsePastedNumber(raw: string, locale: string): number | null {
  const parts = new Intl.NumberFormat(locale).formatToParts(1234567.5);
  const groupChar = parts.find((part) => part.type === 'group')?.value ?? ',';
  const decimalChar = parts.find((part) => part.type === 'decimal')?.value ?? '.';
  const text = raw.replace(/\s/g, '').split(groupChar).join('').replace(decimalChar, '.');
  if (!text) return null;
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
}

/** Clipboard text as rows of trimmed cells; the trailing line break Excel adds is dropped. */
export function parsePastedText(text: string): string[][] {
  const rows = text.split(/\r\n|\r|\n/).map((line) => line.split('\t').map((cell) => cell.trim()));
  while (rows.at(-1)?.every((cell) => cell === '')) {
    rows.pop();
  }
  return rows;
}

/** Sets a dot-path value on `obj`, creating missing parent objects (e.g. `unit.code`). */
// eslint-disable-next-line @typescript-eslint/no-restricted-types -- cell value of any column type
export function setNestedValue(obj: object, path: string, value: unknown): void {
  const keys = path.split('.');
  const lastKey = keys[keys.length - 1];
  // eslint-disable-next-line @typescript-eslint/no-restricted-types -- binding is a runtime dot-path into any row shape
  let current = obj as Record<string, unknown>;
  for (const key of keys.slice(0, -1)) {
    if (!current[key] || typeof current[key] !== 'object') {
      current[key] = {};
    }
    // eslint-disable-next-line @typescript-eslint/no-restricted-types -- grid row bag from kl-grid-form
    current = current[key] as Record<string, unknown>;
  }
  current[lastKey] = value;
}

/** Writes clipboard cell N into `columns[N]`; empty, read-only and non-text cells are skipped without a shift. */
export function writePastedCells<T extends object>(
  row: T,
  columns: KlGridFormColumn<T>[],
  cells: string[],
  locale = 'tr'
): Map<string, KlGridFormPastedValue> {
  const written = new Map<string, KlGridFormPastedValue>();
  for (const [offset, raw] of cells.entries()) {
    const column = columns[offset];
    const value = column ? pastedCellValue(column, raw, row, locale) : null;
    if (value !== null) {
      setNestedValue(row, column.binding, value);
      written.set(column.binding, value);
    }
  }
  return written;
}

/** True when a clipboard row would put something into a new row: a non-empty cell on a column the grid writes or on a picker column. */
export function hasPastableCell<T extends object>(columns: KlGridFormColumn<T>[], cells: string[]): boolean {
  return cells.some((raw, offset) => {
    const column = columns[offset];
    if (!raw || !column || column.isReadOnly) {
      return false;
    }
    return column.type === 'picker' || takesPastedText(column);
  });
}

/** Non-empty pasted codes of `codeBinding` with their rows; rows where the grid wrote `barcodeBinding` are left out, their barcode lookup sets the record. */
export function pastedCodeTargets(
  event: KlGridFormPasteEvent,
  codeBinding: string,
  barcodeBinding?: string
): KlGridFormPastedCodeTarget[] {
  const codes = event.columnsByBinding?.get(codeBinding);
  if (!codes || !event.pastedRows) {
    return [];
  }
  return event.pastedRows
    .map((pastedRow, offset) => ({ code: codes[offset], pastedRow }))
    .filter(({ code, pastedRow }) => code !== '' && (!barcodeBinding || !pastedRow.written.has(barcodeBinding)));
}

/** Writes the pasted values onto `row` again, e.g. after a material lookup rebuilt the row, so they win. */
export function reapplyPastedValues(row: object, pastedRow: KlGridFormPastedRow): void {
  for (const [binding, value] of pastedRow.written) {
    setNestedValue(row, binding, value);
  }
}

function pastedCellValue<T extends object>(
  column: KlGridFormColumn<T>,
  raw: string,
  row: T,
  locale: string
): KlGridFormPastedValue | null {
  if (!raw || !takesPastedText(column) || column.isReadOnly || column.isReadOnlyForRow?.(row)) {
    return null;
  }
  if (column.type === 'number' || column.type === 'decimal') {
    return parsePastedNumber(raw, locale);
  }
  const maxLength = typeof column.maxLength === 'function' ? column.maxLength(row) : column.maxLength;
  return maxLength ? raw.slice(0, maxLength) : raw;
}

function takesPastedText<T extends object>(column: KlGridFormColumn<T>): boolean {
  const isDate = column.format === 'date' || column.format === 'date-time';
  const type = column.type ?? 'text';
  return !isDate && (type === 'text' || type === 'password' || type === 'number' || type === 'decimal');
}
